import type {
  ArchivedReplayLocalState, ArchivedReplayRemoteState, ReplayRow,
} from '@/services/sync/ArchivedPartialReplayCoordinator';

interface RemoteClient {
  from(table: string): any;
  rpc(name: string, args: Record<string, unknown>): PromiseLike<{ data: any; error: any }>;
}
/** Paginação explícita: nunca considera uma página truncada como ausência remota. */
export const readArchivedPartialReplayRemote = async (input: {
  client: RemoteClient;
  local: ArchivedReplayLocalState;
  companyId: string;
  clientBuild: number;
  assertSession(): Promise<void>;
}): Promise<ArchivedReplayRemoteState> => {
  const { client, local, companyId } = input;
  const one = async (table: string, id: string): Promise<ReplayRow | null> => {
    await input.assertSession();
    const response = await client.from(table).select('*').eq('empresa_id', companyId).eq('id', id).maybeSingle();
    if (response.error) throw response.error;
    await input.assertSession();
    return response.data;
  };
  const all = async (table: string, filter: (query: any) => any): Promise<ReplayRow[]> => {
    const rows: ReplayRow[] = [];
    for (let offset = 0; offset < 10000; offset += 500) {
      await input.assertSession();
      const response = await filter(client.from(table).select('*').eq('empresa_id', companyId))
        .order(table === 'guincho_sync_tombstones' ? 'sequence' : 'id', { ascending: true }).range(offset, offset + 499);
      if (response.error) throw response.error;
      if (!Array.isArray(response.data)) throw new Error('archived_replay_remote_read_invalid');
      rows.push(...response.data);
      await input.assertSession();
      if (response.data.length < 500) return rows;
    }
    throw new Error('archived_replay_remote_read_limit');
  };
  const checklist = await one('guincho_checklists', local.archive.item_id);
  const empty = { checklist, template: null, values: [], fields: [], snapshotFields: [], tombstones: [], authorizedLegacyFieldIds: [] };
  if (!checklist || checklist.retired_at != null) return empty;
  const template = await one('guincho_templates_checklist', checklist.template_id);
  const fields = await all('guincho_campos_template', query => query.eq('template_id', checklist.template_id));
  const snapshotFields = checklist.snapshot_id
    ? await all('guincho_checklist_snapshot_fields', query => query.eq('snapshot_id', checklist.snapshot_id)) : [];
  const values = await all('guincho_valores_checklist', query => query.eq('checklist_id', checklist.id));
  // Também verifica UUIDs locais: uma referência transferida não é uma resposta ausente.
  for (let offset = 0; offset < local.values.length; offset += 200) {
    const ids = local.values.slice(offset, offset + 200).map(row => row.id);
    const byId = await all('guincho_valores_checklist', query => query.in('id', ids));
    for (const row of byId) {
      const existing = values.find(value => value.id === row.id);
      if (existing && JSON.stringify(existing) !== JSON.stringify(row)) throw new Error('archived_replay_remote_changed');
      if (!existing) values.push(row);
    }
  }
  const ids = [...new Set([checklist.id, checklist.template_id, ...fields.map(row => row.id),
    ...snapshotFields.map(row => row.original_campo_id), ...local.values.flatMap(row => [row.id, row.campo_id])].filter(Boolean))];
  const relevantIds = new Set(ids);
  const tombstones: ReplayRow[] = [];
  let cursor: string | null = null;
  let target: number | null = null;
  const seenCursors = new Set<string>();
  const seenSequences = new Set<number>();
  let feedComplete = false;
  // A tabela não é acessível à sessão autenticada; a RPC devolve o feed do próprio tenant.
  for (let page = 0; page < 40; page += 1) {
    await input.assertSession();
    const response = await client.rpc('get_sync_tombstones_v2', {
      p_cursor: cursor, p_limit: 500, p_client_build: input.clientBuild,
    });
    if (response.error) throw response.error;
    const rows = response.data;
    if (!Array.isArray(rows) || rows.length === 0 || rows.length > 500 ||
      rows.some(row => !row || typeof row !== 'object' || Array.isArray(row))) {
      throw new Error('archived_replay_tombstone_feed_invalid');
    }
    const first = rows[0];
    const highWatermark = Number(first.target_high_watermark);
    const nextCursor = first.next_cursor;
    const hasMore = first.has_more;
    if (!Number.isSafeInteger(highWatermark) || highWatermark < 0 ||
      (target !== null && highWatermark !== target) ||
      typeof hasMore !== 'boolean' ||
      (nextCursor !== null && (typeof nextCursor !== 'string' || !nextCursor)) ||
      (hasMore && !nextCursor) ||
      rows.some(row => Number(row.target_high_watermark) !== highWatermark ||
        row.next_cursor !== nextCursor || row.has_more !== hasMore)) {
      throw new Error('archived_replay_tombstone_feed_invalid');
    }
    target = highWatermark;
    for (const row of rows) {
      if (row.sequence === null) {
        if (rows.length !== 1 || hasMore || nextCursor !== null) {
          throw new Error('archived_replay_tombstone_feed_invalid');
        }
        continue;
      }
      const sequence = Number(row.sequence);
      if (!Number.isSafeInteger(sequence) || sequence <= 0 || seenSequences.has(sequence) ||
        !row.entity_type || !row.entity_id ||
        !['delete', 'retire'].includes(row.deletion_kind)) {
        throw new Error('archived_replay_tombstone_feed_invalid');
      }
      seenSequences.add(sequence);
      if (row.parent_id === checklist.id || relevantIds.has(row.entity_id)) {
        tombstones.push({
          sequence, event_id: row.event_id, empresa_id: companyId,
          entity_type: row.entity_type, entity_id: row.entity_id,
          parent_id: row.parent_id, deletion_kind: row.deletion_kind,
          metadata: row.metadata, deleted_at: row.deleted_at,
        });
      }
    }
    await input.assertSession();
    if (!hasMore) { feedComplete = true; break; }
    if (seenCursors.has(nextCursor)) throw new Error('archived_replay_tombstone_feed_invalid');
    seenCursors.add(nextCursor);
    cursor = nextCursor;
  }
  if (!feedComplete) throw new Error('archived_replay_tombstone_feed_limit');
  await input.assertSession();
  const authorized = await client.rpc('get_authorized_legacy_fields_v1', {
    p_checklist_id: checklist.id, p_client_build: input.clientBuild,
  });
  if (authorized.error) throw authorized.error;
  if (!Array.isArray(authorized.data)) throw new Error('archived_replay_authorization_read_invalid');
  await input.assertSession();
  // Detecta troca do ponteiro/aposentadoria durante as consultas antes de produzir um plano.
  const finalParent = await one('guincho_checklists', checklist.id);
  if (!finalParent || JSON.stringify(finalParent) !== JSON.stringify(checklist)) throw new Error('archived_replay_parent_changed');
  return { checklist, template, fields, snapshotFields, values: values as ArchivedReplayRemoteState['values'],
    tombstones, authorizedLegacyFieldIds: authorized.data.map((row: { campo_id: string }) => row.campo_id) };
};
